/**
 * Client → server telemetry for E2EE failures.
 *
 * The server endpoint is `POST /api/v1/debug/e2ee-error` (see
 * apps/api/src/routes/debug.routes.ts). Persists into `e2ee_decrypt_errors`.
 *
 * Coverage: every code path that ends in a decrypt-failure placeholder
 * ("[Nachricht konnte nicht entschlüsselt werden]" /
 *  "[Eigene Nachricht – Verschlüsselung aktiv]") OR a sender-side
 * distribute that didn't reach all expected recipient slots should call
 * `reportE2eeError`. The original X3DH-receive-fallback report in
 * useCrypto-encrypt.ts predates this helper and uses naked fetch — kept
 * untouched on purpose.
 *
 * Fire-and-forget: the call must never throw into the decrypt path.
 * Per-user rate-limit (30/60s) lives server-side; we additionally
 * de-duplicate identical reports for 30s in-memory so a re-render storm
 * over the same failed message produces one row, not dozens.
 */
import { api } from "./api";
import { e2eeLog } from "./e2ee-logger";

export type E2eePhase =
  | "x3dh_receive"
  | "dm_no_session"
  | "dm_decrypt_failed"
  | "group_no_key"
  // KOL-82: Envelope entschlüsselt, aber der Schlüssel landete nicht im Speicher —
  // dieser Pfad war vorher komplett signalfrei und hielt die Ack-Schleife am Leben.
  | "pending_key_not_stored"
  // Envelope ließ sich nicht entschlüsseln (meist: keine pairwise Session,
  // weil der Absender im Ratchet-Format verteilt) — NICHT dasselbe wie
  // "entschlüsselt, aber nicht gespeichert".
  | "pending_key_undecryptable"
  | "pending_key_decrypt_failed"
  | "group_replay"
  | "group_keyid_rotated"
  | "group_iter_too_far_ahead"
  | "group_decrypt_failed"
  | "distribute_partial"
  | "distribute_per_device_fail"
  | "distribute_no_devices"
  | "distribute_keys_fetch_failed"
  | "opk_pool_empty"
  | "opk_persist_verify_failed"
  | "self_canary_failed"
  | "fanout_envelope_dropped"
  // F23b: Zero-Active-Devices Redistribute telemetry
  | "redistribute_request_sent"       // client emitted request_channel_redistribute
  | "redistribute_request_received"   // member received proxy_redistribute_request and redistributed
  | "redistribute_request_throttled"  // client skipped emit (already sent this session)
  // F22c: channelRecoveryKey peer-pull phases
  | "recovery_key_request_sent"       // client sent WS event (after throttle guard)
  | "recovery_key_request_proxy_received" // online member received the fan-out
  | "recovery_key_redistributed"      // member had the key and started distribute
  | "recovery_key_no_holder"          // no member had the key locally
  | "recovery_key_received"           // requester stored the key (in processSenderKeyPlaintext)
  // Bug D: user_backup_key auto-pull telemetry
  | "backup_key_request_sent"
  | "backup_key_request_received_proxy"
  | "backup_key_distributed"
  | "backup_key_received"
  | "backup_key_no_peer_has_it"
  // P1: device re-registration self-heal forensics
  | "reregister_attempt"
  // Task 6: web key-store wipe detection + constrained auto re-key
  | "e2ee_store_wipe_detected"
  // F22c Task 5: wrap-time drift guard — own published bundle diverged from local SPK
  | "wrap_drift_guard_tripped"
  // SPK keyId-uniqueness: receiver skipped an undecryptable X3DH wrap (unknown keyId
  // or legacy-keyId collision) without feeding the cascade-detector
  | "x3dh_spk_drift_skipped"
  // Stale/cached-bundle: sender used a one-time pre-key we no longer hold; skipped +
  // session-reset requested instead of feeding the cascade-detector
  | "x3dh_stale_bundle_skipped"
  // P1.4: stale-SPK bucketing — sender used a historical (rotated) SPK confirmed by
  // keyId-in-previousSignedPreKeys or pub-snippet mismatch → stale bundle, NOT local
  // corruption → not counted in the cascade-detector.
  | "x3dh_stale_spk_skipped"
  // P1.4: old client (no usedSignedPreKeyPubSnippet in envelope) — cannot distinguish
  // stale from genuine; conservatively skipped in the cascade-detector, still reported
  // so the volume is visible.
  | "x3dh_nosnippet_skipped"
  // In-memory deviceKeys lagged IDB (cross-tab OPK replenish); re-read IDB recovered
  // the OPK that an in-memory lookup missed → decrypt succeeds instead of false drop
  | "x3dh_opk_idb_recovered"
  // Fix 3: last-resort / fallback pre-key was consumed (OPK pool was empty)
  | "lrk_used"
  // Send guard refused to encrypt because local crypto health is flagged broken
  | "send_blocked_broken"
  // Runtime corruption detector flagged the device broken after cross-sender fails
  | "runtime_corruption_detected"
  // Identity-reset flow rotated the device onto a fresh id (emitted by useChat)
  | "identity_reset_rotate"
  // Group send aborted: an envelope was missing its senderDeviceId (useCrypto-group)
  | "group_no_senderdeviceid"
  // P0.2: persist-before-ack failures — ratchet state not committed to IDB after decrypt
  | "session_persist_fail"   // saveSessionOrThrow threw (IDB quota / timeout)
  | "session_idb_miss";      // in-memory session had no IDB record (prior silent save failure)

export interface E2eeErrorReport {
  phase: E2eePhase;
  /** UUID of the message sender (peer for incoming, self for outgoing). */
  senderId?: string | null;
  /** Sender's device that produced the envelope, if known. */
  senderDeviceId?: string | null;
  /** Our local device id. */
  deviceId?: string | null;
  /** Free-text error / context (channelId, expected vs reached counts, ...). */
  error?: string;
}

const DEDUPE_WINDOW_MS = 30_000;
const recent = new Map<string, number>();

const reportedErrors = new Set<string>();

let consecutive429s = 0;
let telemetryPausedUntil = 0;
let _backoffMs = 30_000;

function dedupeKey(r: E2eeErrorReport): string {
  return [r.phase, r.senderId ?? "", r.senderDeviceId ?? "", r.deviceId ?? "", r.error ?? ""].join("|");
}

/** Server schema rejects nulls — strip nullish fields before posting. */
function clean(r: E2eeErrorReport): Record<string, unknown> {
  const out: Record<string, unknown> = { phase: r.phase };
  if (r.senderId) out.senderId = r.senderId;
  if (r.senderDeviceId) out.senderDeviceId = r.senderDeviceId;
  if (r.deviceId) out.deviceId = r.deviceId;
  if (r.error) out.error = r.error;
  return out;
}

export function reportE2eeError(report: E2eeErrorReport & { msgId?: string }): void {
  try {
    if (Date.now() < telemetryPausedUntil) return;

    const msgId = report.msgId ?? "";
    const perMsgKey = `${msgId}:${report.phase}`;
    if (msgId && reportedErrors.has(perMsgKey)) return;
    if (msgId) reportedErrors.add(perMsgKey);

    const now = Date.now();
    const key = dedupeKey(report);
    const last = recent.get(key);
    if (last !== undefined && now - last < DEDUPE_WINDOW_MS) return;
    recent.set(key, now);
    // GC: drop entries older than the window so the map can't grow unbounded
    // on long-running sessions. Cheap because reports are rare.
    if (recent.size > 200) {
      for (const [k, t] of recent) {
        if (now - t > DEDUPE_WINDOW_MS) recent.delete(k);
      }
    }

    // Truncate error to fit the server's z.string().max(2000) schema.
    const { msgId: _msgId, ...rest } = report as E2eeErrorReport & { msgId?: string };
    const truncated: E2eeErrorReport = rest.error && rest.error.length > 1900
      ? { ...rest, error: rest.error.slice(0, 1900) }
      : rest;
    const body = clean(truncated);

    // raw:true → don't try to parse the 204 response as JSON.
    void api.post("/debug/e2ee-error", body, { raw: true }).then(() => {
      consecutive429s = 0;
      _backoffMs = 30_000;
    }).catch((err: unknown) => {
      const status = (err as { status?: number })?.status;
      if (status === 429) {
        consecutive429s += 1;
        if (consecutive429s >= 5) {
          telemetryPausedUntil = Date.now() + _backoffMs;
          _backoffMs = Math.min(_backoffMs * 2, 600_000);
        }
      }
      e2eeLog.warn("reportE2eeError failed", err);
    });
  } catch (err) {
    e2eeLog.warn("reportE2eeError threw", err);
  }
}
